import React, { useState } from "react";
import { useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faHandSpock,
  faHandHoldingHeart,
  faUser,
  faCheckCircle,
  faFileContract,
  faChartLine,
  faHeart,
  faUsers,
  faRupeeSign,
} from "@fortawesome/free-solid-svg-icons";

const MahilaMandiSupportAMaker = () => {
  const [donationAmount, setDonationAmount] = useState("");
  const [selectedEntrepreneur, setSelectedEntrepreneur] = useState(null);
  const [agree, setAgree] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");

  // Dummy entrepreneur data for women-led enterprises
  const entrepreneurs = [
    {
      id: 1,
      name: "Priya Sharma",
      location: "Surak, Gujarat, India",
      need: "Yarn & Dyes for Weaving",
      goal: 15000,
      raised: 6500,
      story: "Priya started her weaving cooperative with 5 women from her village. Your support will help them purchase dyes and yarn to create 200 new scarves for export, doubling their income."
    },
    {
      id: 2,
      name: "Aisha Khan",
      location: "Jaipur, Rajasthan, India",
      need: "Beads & Silver Wire for Jewelry",
      goal: 25000,
      raised: 12000,
      story: "Aisha empowers 15 local artisans through her jewelry workshop. Funds will buy materials for a new collection, enabling them to reach international markets and gain financial independence."
    },
    {
      id: 3,
      name: "Lila Devi",
      location: "Chennai, Tamil Nadu, India",
      need: "Clay & Glazes for Pottery",
      goal: 18000,
      raised: 9000,
      story: "Lila's pottery group crafts eco-friendly homeware. With your help, they can acquire glazes to produce 100 unique vases, boosting sales and supporting 10 families in her community."
    },
    {
      id: 4,
      name: "Sunita Patel",
      location: "Kolkata, West Bengal, India",
      need: "Essential Oils & Wax for Soaps",
      goal: 12000,
      raised: 6000,
      story: "Sunita's soap-making circle uses natural ingredients to create wellness products. Donations will fund oils for 500 bars, helping her team expand to local spas and online sales."
    },
    {
      id: 5,
      name: "Meera Singh",
      location: "Delhi, India",
      need: "Spices & Jars for Pickles",
      goal: 10000,
      raised: 4500,
      story: "Meera leads a pickle-making initiative with 8 homemakers. Your contribution will supply jars and spices for 300 batches, turning their recipes into a sustainable business."
    },
    {
      id: 6,
      name: "Rani Gupta",
      location: "Mumbai, Maharashtra, India",
      need: "Herbs & Packaging for Teas",
      goal: 22000,
      raised: 11000,
      story: "Rani's herbal tea blend project sources from local growers. Funds will provide packaging for 1,000 pouches, enabling her enterprise to enter health stores and online platforms."
    },
    {
      id: 7,
      name: "Kavita Rao",
      location: "Bangalore, Karnataka, India",
      need: "Wool & Looms for Textiles",
      goal: 30000,
      raised: 15000,
      story: "Kavita's textile collective weaves sustainable fabrics. Support will acquire looms for 20 new members, creating jobs and preserving traditional crafts for future generations."
    },
    {
      id: 8,
      name: "Sita Devi",
      location: "Hyderabad, Telangana, India",
      need: "Bamboo & Tools for Handicrafts",
      goal: 16000,
      raised: 7000,
      story: "Sita's handicraft group crafts eco-baskets from bamboo. Your donation will buy tools for 12 artisans, helping them scale production and enter fair trade markets."
    },
  ];

  // Derived crowdfunding dashboard data
  const totalRaised = entrepreneurs.reduce((sum, ent) => sum + ent.raised, 0);
  const totalGoal = entrepreneurs.reduce((sum, ent) => sum + ent.goal, 0);
  const totalEnterprises = entrepreneurs.length;
  const enterprisesFunded = entrepreneurs.filter(ent => ent.raised >= ent.goal).length;
  const avgRaised = totalRaised / totalEnterprises;

  const handleDonate = (entrepreneur) => {
    if (!donationAmount || !agree) {
      setSuccessMsg("⚠️ Please enter an amount and agree to Terms & Conditions.");
      return;
    }
    setSelectedEntrepreneur(entrepreneur);
    setSuccessMsg(
      `✅ Thank you! Your support of ₹${donationAmount} has been pledged to ${entrepreneur.name}.`
    );
    setDonationAmount("");
    setAgree(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 bg-black text-white min-h-screen">
      <h1 className="text-2xl font-bold bg-rose-700 px-4 py-2 rounded-lg text-rose-100 flex items-center gap-2 mb-2">
        <FontAwesomeIcon icon={faHandSpock} /> Empower a Woman Entrepreneur
      </h1>
      <p className="text-gray-100 mb-8">
        Join our mission to empower women-led enterprises worldwide. Your contribution helps
        them access raw materials, tools, training, and resources to scale their handicrafts,
        soaps, pickles, and more. Every small step ignites big dreams.
      </p>

      {/* Crowdfunding Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <div className="bg-rose-900 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-rose-200">Total Raised</h3>
          <p className="text-2xl font-bold text-rose-100">₹{totalRaised.toLocaleString()}</p>
        </div>
        <div className="bg-rose-900 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-rose-200">Enterprises Supported</h3>
          <p className="text-2xl font-bold text-rose-100">{totalEnterprises}</p>
        </div>
        <div className="bg-rose-900 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-rose-200">Fully Funded</h3>
          <p className="text-2xl font-bold text-rose-100">{enterprisesFunded}</p>
        </div>
        <div className="bg-rose-900 p-4 rounded-lg">
          <h3 className="text-sm font-medium text-rose-200">Avg Raised per Enterprise</h3>
          <p className="text-2xl font-bold text-rose-100">₹{Math.round(avgRaised).toLocaleString()}</p>
        </div>
      </div>

      {/* Entrepreneur Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {entrepreneurs.map((entrepreneur) => {
          const percentage = Math.min(
            100,
            Math.round((entrepreneur.raised / entrepreneur.goal) * 100)
          );
          return (
            <div
              key={entrepreneur.id}
              className="bg-white p-4 rounded-xl shadow hover:shadow-lg transition"
            >
              <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
                <FontAwesomeIcon icon={faUser} /> {entrepreneur.name}
              </h2>
              <p className="text-sm text-gray-500">{entrepreneur.location}</p>
              <p className="mt-2 text-gray-700">
                <strong>Need:</strong> {entrepreneur.need}
              </p>
              <p className="text-gray-600 text-sm">
                Goal: ₹{entrepreneur.goal.toLocaleString()} | Raised: ₹
                {entrepreneur.raised.toLocaleString()}
              </p>

              {/* Progress bar */}
              <div className="w-full bg-gray-200 rounded-full h-3 mt-2">
                <div
                  className="bg-rose-600 h-3 rounded-full"
                  style={{ width: `${percentage}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-500 mt-1">{percentage}% funded</p>

              {/* Impact Story */}
              <div className="mt-3 p-3 bg-rose-50 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-800 mb-1">Impact Story</h4>
                <p className="text-xs text-gray-600 line-clamp-3">{entrepreneur.story}</p>
              </div>

              {/* Donation Input */}
              <div className="mt-4">
                <input
                  type="number"
                  placeholder="Enter amount (₹)"
                  value={donationAmount}
                  onChange={(e) => setDonationAmount(e.target.value)}
                  className="border-2 rounded-lg px-3 py-2 w-full text-sm mb-2 text-black"
                />
                <label className="flex items-center gap-2 text-sm text-gray-600">
                  <input
                    type="checkbox"
                    checked={agree}
                    onChange={() => setAgree(!agree)}
                  />
                  I agree to the{" "}
                  <span className="text-rose-600 underline cursor-pointer">
                    Terms & Conditions
                  </span>
                </label>
                <button
                  onClick={() => handleDonate(entrepreneur)}
                  className="w-full mt-3 bg-rose-600 hover:bg-rose-700 text-white py-2 rounded-lg flex items-center justify-center gap-2"
                >
                  <FontAwesomeIcon icon={faHandHoldingHeart} /> Empower
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Success message */}
      {successMsg && (
        <div className="mt-6 p-4 bg-rose-100 border border-rose-300 rounded-lg text-rose-800 flex items-center gap-2">
          <FontAwesomeIcon icon={faCheckCircle} /> {successMsg}
        </div>
      )}

      {/* Terms & Conditions Section */}
      <div className="mt-4 mb-4 bg-white shadow p-6 rounded-xl">
        <h3 className="text-xl font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <FontAwesomeIcon icon={faFileContract} /> Terms & Conditions
        </h3>
        <ul className="list-disc pl-5 text-sm text-gray-600 space-y-2">
          <li>
            Donations are voluntary and non-refundable. We only act as a
            platform connecting supporters with women entrepreneurs.
          </li>
          <li>
            Funds will be allocated to the entrepreneur's stated needs (e.g., raw materials,
            tools, training, packaging).
          </li>
          <li>
            We aim for transparency but cannot guarantee outcomes due to
            business risks (market fluctuations, supply issues, etc.).
          </li>
          <li>
            Supporters may receive periodic updates/photos on fund utilization.
          </li>
          <li>
            This platform is not a money-lending service. It is purely for
            social empowerment of women enterprises.
          </li>
        </ul>
      </div>
      {/* Footer */}
      <footer className="bg-rose-700 text-white py-10 px-4 sm:px-6 md:px-20">
        <div className="flex flex-col md:flex-row justify-between items-center text-center md:text-left">
          <p className="text-sm">© 2025 Think5Whys Global DeepTech Solutions. All rights reserved.</p>
          <div className="flex space-x-6 mt-4 md:mt-0 text-sm">
            <Link to="/about-us" className="text-white hover:text-rose-300 transition">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-rose-300 transition">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-rose-300 transition">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-rose-300 transition">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default MahilaMandiSupportAMaker;